import React, { useState } from "react";

export default function AnimatedLogout({ onLogout }) {
  const [isClicked, setIsClicked] = useState(false);
  const [isSlammed, setIsSlammed] = useState(false);

  const handleLogout = () => {
    if (isClicked) return; // Prevent double clicks
    
    setIsClicked(true);

    // Timeline for the animation
    // 1. Man walks to the door (approx 1.2s)
    // 2. Door slams shut
    // 3. Callback to parent logout function
    
    setTimeout(() => {
      setIsSlammed(true);
      setTimeout(() => {
        if (onLogout) onLogout();
      }, 500);
    }, 1500);
  };

  return (
    <div className="logout-wrapper">
      <style>{`
        .logoutButton {
          --figure-duration: 100ms;
          --walking-duration: 100ms;
          background: #1e293b; /* Premium Dark Slate */
          border: 0;
          color: #f4f7ff;
          cursor: pointer;
          display: flex;
          align-items: center;
          font-family: "Inter", sans-serif;
          font-size: 14px;
          font-weight: 700;
          height: 48px;
          outline: none;
          padding: 0 20px;
          perspective: 100px;
          position: relative;
          width: 100%;
          border-radius: 14px;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .logoutButton:hover {
          background: #0f172a;
          transform: translateY(-2px);
        }

        .logoutButton:hover .door {
          transform: rotateY(20deg);
        }

        .logoutButton.clicked .button-text {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .logoutButton.clicked .door {
          transform: rotateY(35deg);
          transition: transform 0.4s ease;
        }

        .logoutButton.door-slammed .door {
          transform: rotateY(0deg) !important;
          transition: transform 100ms ease-in !important;
        }

        .button-text {
          color: #f4f7ff;
          position: relative;
          z-index: 10;
        }

        svg {
          display: block;
          position: absolute;
        }

        .figure {
          bottom: 8px;
          fill: #0072ff; /* Travelo Blue */
          right: 25px;
          width: 28px;
          z-index: 4;
          transition: transform 1.5s cubic-bezier(0.2, 0.1, 0.8, 0.9), opacity 0.2s;
        }

        .clicked .figure {
          transform: translateX(18px); /* Moves the man into the door */
        }

        .door-slammed .figure {
          opacity: 0;
        }

        .door, .doorway {
          bottom: 6px;
          fill: #f4f7ff;
          right: 15px;
          width: 32px;
        }

        .door {
          transform-origin: 100% 50%;
          transform-style: preserve-3d;
          transition: transform 200ms ease;
          z-index: 5;
        }

        .door path {
          fill: #0072ff;
          stroke: #0072ff;
          stroke-width: 4;
        }

        .doorway {
          z-index: 3;
        }

        /* --- Walking Animation --- */
        .clicked .arm1, .clicked .arm2, .clicked .leg1, .clicked .leg2 {
          animation: walking 0.2s infinite alternate ease-in-out;
        }

        .clicked .arm2, .clicked .leg1 {
          animation-delay: 0.1s;
        }

        @keyframes walking {
          from { transform: rotate(-20deg); }
          to { transform: rotate(20deg); }
        }

        .arm1, .arm2 { transform-origin: 52% 45%; }
        .leg1, .leg2 { transform-origin: 47% 64.5%; }

        @media (max-width: 900px) {
          .logout-wrapper { width: auto; margin-left: auto; }
          .logoutButton { width: 130px; }
          .logoutButton .button-text { display: none; }
          .logoutButton { justify-content: center; padding: 0; }
        }
      `}</style>

      <button 
        className={`logoutButton ${isClicked ? 'clicked' : ''} ${isSlammed ? 'door-slammed' : ''}`}
        onClick={handleLogout}
      >
        {/* Door Frame */}
        <svg className="doorway" viewBox="0 0 100 100">
          <path d="M93.4 86.3H58.6c-1.9 0-3.4-1.5-3.4-3.4V17.1c0-1.9 1.5-3.4 3.4-3.4h34.8c1.9 0 3.4 1.5 3.4 3.4v65.8c0 1.9-1.5 3.4-3.4 3.4z" />
        </svg>

        {/* The Walking Figure */}
        <svg className="figure" viewBox="0 0 100 100">
          <circle cx="52.1" cy="32.4" r="6.4" />
          <path d="M50.7 62.8c-1.2 2.5-3.6 5-7.2 4-3.2-.9-4.9-3.5-4-7.8.7-3.4 3.1-13.8 4.1-15.8 1.7-3.4 1.6-4.6 7-3.7 4.3.7 4.6 2.5 4.3 5.4-.4 3.7-2.8 15.1-4.2 17.9z" />
          <g className="arm1"><path d="M55.5 56.5l-6-9.5c-1-1.5-.6-3.5.9-4.4 1.5-1 3.7-1.1 4.6.4l6.1 10c1 1.5.3 3.5-1.1 4.4z" /></g>
          <g className="arm2"><path d="M34.2 43.6L45 40.3c1.7-.6 3.5.3 4 2 .6 1.7-.3 4-2 4.5l-10.8 2.8c-1.7.6-3.5-.3-4-2z" /></g>
          <g className="leg1"><path d="M52.1 73.2s-7-5.7-7.9-6.5c-.9-.9-1.2-3.5-.1-4.9 1.1-1.4 3.8-1.9 5.2-.9l7.9 7c1.4 1.1 1.7 3.5.7 4.9z" /></g>
          <g className="leg2"><path d="M37.8 72.7s1.3-10.2 1.6-11.4 2.4-2.8 4.1-2.6c1.7.2 3.6 2.3 3.4 4l-1.8 11.1c-.2 1.7-1.7 3.3-3.4 3.1z" /></g>
        </svg>

        {/* The Moving Door */}
        <svg className="door" viewBox="0 0 100 100">
          <path d="M93.4 86.3H58.6c-1.9 0-3.4-1.5-3.4-3.4V17.1c0-1.9 1.5-3.4 3.4-3.4h34.8c1.9 0 3.4 1.5 3.4 3.4v65.8c0 1.9-1.5 3.4-3.4 3.4z" />
          <circle cx="66" cy="50" r="3.7" />
        </svg>

        <span className="button-text">Log Out</span>
      </button>
    </div>
  );
}